import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Box,
    Typography,
    Grid,
    TextField,
    Pagination,
    CircularProgress,
    Alert,
    Stack,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Button,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { format } from 'date-fns'
import { MyShift, GetMyShiftsParams } from '@/core/models/planning.model'
import ShiftCard from './ShiftCard'

interface MyShiftsListProps {
    shifts: MyShift[]
    isLoading: boolean
    error: any
    totalItems: number
    page: number
    limit: number
    onPageChange: (page: number) => void
    onFilterChange: (filters: Partial<GetMyShiftsParams>) => void
    onReleaseShift: (shiftId: string) => void
    filters: Partial<GetMyShiftsParams>
}

export function MyShiftsList({
    shifts,
    isLoading,
    error,
    totalItems,
    page,
    limit,
    onPageChange,
    onFilterChange,
    onReleaseShift,
    filters
}: MyShiftsListProps) {
    const { t } = useTranslation()
    const [startDate, setStartDate] = useState<Date | null>(filters.startDate ? new Date(filters.startDate) : null)
    const [endDate, setEndDate] = useState<Date | null>(filters.endDate ? new Date(filters.endDate) : null)
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
    const [selectedShiftId, setSelectedShiftId] = useState<string | null>(null)

    const handleStartDateChange = (date: Date | null) => {
        setStartDate(date)
        if (date) {
            onFilterChange({ ...filters, startDate: format(date, 'yyyy-MM-dd') })
        } else {
            const { startDate, ...rest } = filters
            onFilterChange(rest)
        }
    }

    const handleEndDateChange = (date: Date | null) => {
        setEndDate(date)
        if (date) {
            onFilterChange({ ...filters, endDate: format(date, 'yyyy-MM-dd') })
        } else {
            const { endDate, ...rest } = filters
            onFilterChange(rest)
        }
    }

    const totalPages = Math.ceil(totalItems / limit)

    const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
        onPageChange(value)
    }

    const handleReleaseClick = (shiftId: string) => {
        setSelectedShiftId(shiftId)
        setConfirmDialogOpen(true)
    }

    const handleConfirmRelease = () => {
        if (selectedShiftId) {
            onReleaseShift(selectedShiftId)
            setConfirmDialogOpen(false)
            setSelectedShiftId(null)
        }
    }

    const handleCancelRelease = () => {
        setConfirmDialogOpen(false)
        setSelectedShiftId(null)
    }

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                <CircularProgress />
            </Box>
        )
    }

    if (error) {
        return (
            <Alert severity="error">
                {t('page.shiftMarketplace.errorLoadingData', 'Error loading data. Please try again.')}
            </Alert>
        )
    }

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Accordion defaultExpanded={false} sx={{ mb: 1 }}>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="filter-panel-content"
                    id="filter-panel-header"
                >
                    <Typography variant="subtitle1">
                        {t('page.shiftMarketplace.filters', 'Filters')}
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    label={t('page.shiftMarketplace.startDate', 'Start Date')}
                                    value={startDate}
                                    onChange={(date: Date | null) => handleStartDateChange(date)}
                                    renderInput={(params) => (
                                        <TextField {...params} fullWidth size="small" />
                                    )}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    label={t('page.shiftMarketplace.endDate', 'End Date')}
                                    value={endDate}
                                    onChange={(date: Date | null) => handleEndDateChange(date)}
                                    renderInput={(params) => (
                                        <TextField {...params} fullWidth size="small" />
                                    )}
                                />
                            </LocalizationProvider>
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>

            <Box sx={{ flexGrow: 1, overflow: 'auto', pb: 2 }}>
                {shifts.length === 0 ? (
                    <Alert severity="info">
                        {t('page.shiftMarketplace.noMyShiftsFound', 'You have no claimed shifts for the selected period.')}
                    </Alert>
                ) : (
                    <Grid container spacing={2}>
                        {shifts.map((shift) => (
                            <Grid item xs={12} sm={6} md={4} key={shift.shiftId}>
                                <ShiftCard
                                    shift={shift}
                                    type="my"
                                    onAction={() => handleReleaseClick(shift.shiftId)}
                                />
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Box>

            {shifts.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={handlePageChange}
                        color="primary"
                        size="small"
                    />
                </Box>
            )}

            <Dialog
                open={confirmDialogOpen}
                onClose={handleCancelRelease}
                aria-labelledby="release-shift-dialog-title"
                aria-describedby="release-shift-dialog-description"
            >
                <DialogTitle id="release-shift-dialog-title">
                    {t('page.shiftMarketplace.confirmRelease', 'Confirm Release')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="release-shift-dialog-description">
                        {t('page.shiftMarketplace.confirmReleaseMessage', 'Are you sure you want to release this shift? This action cannot be undone.')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelRelease}>
                        {t('common:button.cancel.label')}
                    </Button>
                    <Button onClick={handleConfirmRelease} color="primary" autoFocus>
                        {t('page.shiftMarketplace.release', 'Release')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}

export default MyShiftsList
